/**
 * useGeoLocation — best-effort visitor geolocation hook.
 *
 * Strategy:
 *  1) Resolve via the free `ipapi.co` endpoint (no key, returns
 *     {country_code, city, region}).  Cached in sessionStorage so we
 *     don't hammer the API on every render.
 *  2) Fallback to browser `navigator.geolocation` reverse-lookup via
 *     OpenStreetMap Nominatim (free) if the IP lookup fails.
 *
 * The hook returns the best-matching CARTAL `cityId` (from our
 * locations registry) so consumers can pre-select it in the picker.
 *
 *   const { detectedCityId, country, loading } = useGeoLocation();
 *
 * No PII is stored: only the resolved CARTAL city slug is cached.
 */
import { useEffect, useState } from "react";
import { ALL_CITIES, COUNTRIES } from "../data/locations";

const CACHE_KEY = "cartal:geo:city";
const CACHE_COUNTRY_KEY = "cartal:geo:country";
// Map ipapi/Nominatim country codes to CARTAL country codes (1:1 in most cases)
const COUNTRY_CODE_MAP = {
  SA: "SA", AE: "AE", KW: "KW", QA: "QA", BH: "BH", OM: "OM",
  JO: "JO", EG: "EG", IQ: "IQ", YE: "YE",
};

function normalise(s) {
  return (s || "").toString().trim().toLowerCase();
}

/** Find the CARTAL city that best matches the given country code + city name. */
function matchCity(countryCode, cityName) {
  if (!countryCode) return null;
  const code = COUNTRY_CODE_MAP[countryCode.toUpperCase()];
  if (!code) return null;
  const country = COUNTRIES.find((c) => c.code === code);
  if (!country) return null;
  // Try exact / contains match (Arabic or English)
  const cName = normalise(cityName);
  if (cName) {
    for (const region of country.regions) {
      for (const city of region.cities) {
        if (
          normalise(city.en) === cName ||
          normalise(city.ar) === cName ||
          normalise(city.id) === cName ||
          normalise(city.en).includes(cName) ||
          cName.includes(normalise(city.en))
        ) {
          return city.id;
        }
      }
    }
  }
  // Fallback: capital / first city of that country
  return country.regions[0]?.cities[0]?.id || null;
}

export function useGeoLocation({ autoDetect = true } = {}) {
  const [detectedCityId, setDetectedCityId] = useState(null);
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!autoDetect || typeof window === "undefined") return;
    // Hit cache first
    try {
      const cached = window.sessionStorage.getItem(CACHE_KEY);
      const cachedCountry = window.sessionStorage.getItem(CACHE_COUNTRY_KEY);
      if (cached) {
        setDetectedCityId(cached);
        if (cachedCountry) setCountry(cachedCountry);
        return;
      }
    } catch { /* private mode — ignore */ }

    let cancelled = false;
    setLoading(true);

    const detect = async () => {
      try {
        // Step 1: IP-based lookup (fast, no permission prompt)
        const res = await fetch("https://ipapi.co/json/", { cache: "no-store" });
        if (!res.ok) throw new Error(`ipapi ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        const cityId = matchCity(data.country_code, data.city);
        if (cityId) {
          setDetectedCityId(cityId);
          setCountry(data.country_code);
          try {
            window.sessionStorage.setItem(CACHE_KEY, cityId);
            window.sessionStorage.setItem(CACHE_COUNTRY_KEY, data.country_code);
          } catch { /* ignore */ }
          return;
        }
        throw new Error("no-match");
      } catch (e) {
        if (cancelled) return;
        setError(e.message || "geo-failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    detect();
    return () => { cancelled = true; };
  }, [autoDetect]);

  return { detectedCityId, country, loading, error };
}

export default useGeoLocation;
